import { LEAVE_TYPE_STYLE } from './leave-type-palette'

/**
 * Shared formatting helpers for the manager team roster surfaces
 * (RosterRow, PersonModal). One definition each so the components never
 * drift on number formatting, flag-code humanising, or unknown-leave-type
 * fallbacks.
 */

/**
 * Column template shared by the roster column header and every RosterRow, so
 * the two render as one aligned table (values line up down each column for
 * across-the-team comparison). Columns: Person | Team | Next leave.
 * `minmax(0, …)` lets cells shrink and truncate instead of forcing horizontal
 * overflow on a narrow viewport.
 */
export const ROSTER_GRID_TEMPLATE =
  'minmax(0, 1.7fr) minmax(0, 1fr) minmax(0, 1.1fr)'
/** Horizontal gap between roster columns (px). Header + rows must match. */
export const ROSTER_GRID_GAP = 16
/** Inner horizontal padding for the header + rows (px) so columns align. */
export const ROSTER_ROW_PAD_X = 14

/** Format a number to at most 1 decimal, dropping a trailing `.0`. */
export function fmtNum(n: number): string {
  const rounded = Math.round(n * 10) / 10
  return rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(1)
}

/** Replace underscores/dashes with spaces and Title Case each word. */
export function humaniseFlagCode(code: string): string {
  return code.replace(/[_-]/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())
}

/** The subset of a leave-type style the roster surfaces read. */
export interface RosterLeaveStyle {
  swatch: string
  short: string
  label: string
}

/**
 * Return the leave-type style for `id`, or a neutral fallback (raw code as
 * label) when the wire value isn't a known LeaveTypeId — guards against an
 * unknown type crashing the row.
 */
export function safeLeaveStyle(id: string): RosterLeaveStyle {
  if (id in LEAVE_TYPE_STYLE) {
    const s = LEAVE_TYPE_STYLE[id as keyof typeof LEAVE_TYPE_STYLE]
    return { swatch: s.swatch, short: s.short, label: s.label }
  }
  return { swatch: 'var(--text-3)', short: id.slice(0, 4), label: id }
}
